import log from 'loglevel';

/** Set `localStorage['tt:debug:doBoxForm'] = '1'` and reload to trace the create-task dialog: lines tagged
 *  `[doBoxForm]` appear in the console at the default level (loglevel routes debug to console.log). */
export const DOBOX_FORM_TRACE_STORAGE_KEY = 'tt:debug:doBoxForm';
const FLAG_ON = '1';
const LOGGER_NAME = 'doBoxFormTrace';
const TAG = '[doBoxForm]';
const STACK_FRAMES = 5;
const NO_STACK = '';

function readFlag(): boolean {
  try {
    return localStorage.getItem(DOBOX_FORM_TRACE_STORAGE_KEY) === FLAG_ON;
  } catch {
    return false;
  }
}

const traceLogger = log.getLogger(LOGGER_NAME);
const isEnabled = readFlag();
traceLogger.setLevel(isEnabled ? log.levels.DEBUG : log.levels.SILENT, false);

let nextInstanceId = 0;
const liveInstances = new Set<number>();

export function isDoBoxFormTraceEnabled(): boolean {
  return isEnabled;
}

export function newDoBoxFormInstanceId(): number {
  nextInstanceId += 1;
  return nextInstanceId;
}

function captureStack(): string {
  if (!isEnabled) {
    return NO_STACK;
  }
  return (new Error('trace').stack ?? NO_STACK)
    .split('\n')
    .slice(3, 3 + STACK_FRAMES)
    .map((line) => line.trim())
    .join(' <- ');
}

export function traceDoBoxForm(
  event: string,
  detail: Record<string, unknown> = {},
  options: { withStack?: boolean } = {},
): void {
  if (!isEnabled) {
    return;
  }
  traceLogger.debug(TAG, event, {
    t: Math.round(performance.now()),
    live: [...liveInstances],
    ...detail,
    ...(options.withStack ? { stack: captureStack() } : {}),
  });
}

export function traceDoBoxFormMount(instanceId: number): () => void {
  liveInstances.add(instanceId);
  traceDoBoxForm('modal:mount', {
    instanceId,
    dialogsInDom: document.querySelectorAll('[role="dialog"]').length,
  });
  return () => {
    liveInstances.delete(instanceId);
    traceDoBoxForm('modal:unmount', { instanceId });
  };
}

export function traceDoBoxFormFocus(instanceId: number): () => void {
  if (!isEnabled) {
    return () => undefined;
  }
  const onFocusIn = (event: FocusEvent) => {
    const target = event.target as HTMLElement | null;
    traceDoBoxForm('focusin', {
      instanceId,
      tag: target?.tagName,
      placeholder: target?.getAttribute('placeholder'),
    });
  };
  document.addEventListener('focusin', onFocusIn);
  return () => document.removeEventListener('focusin', onFocusIn);
}
